import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { SurveyFormData } from '../SurveyFormCard';
import { Team } from '../../../api/queries';
import { useGetWorkEnvironmentTemplatesQuery } from '../../../hooks/queries/useGetWorkEnvironmentTemplatesQuery';
import { useGetSurveyTemplatesQuery } from '../../../hooks/queries/useGetSurveyTemplatesQuery';
import { useGetCustomSurveyTemplatesQuery } from '../../../hooks/queries/useGetCustomSurveyTemplatesQuery';
import { GhostLoader } from '../../GhostLoader';
import { useFeatureFlag } from '../../../contexts/FeatureFlagContext';
import { WizardField } from '../WizardField/WizardField';
import { defaultTemplateVersion, templateVersionLabel, templateVersionQuestions } from './hmsTemplateView';
import classes from './SurveySetup.module.css';

type SurveySetupProps = {
  handleChange: (
    name: string,
    value: string | number | boolean | string[] | undefined,
  ) => void;
  formData: SurveyFormData;
  teams: Team[];
  errors: Record<string, string> | undefined;
  /** «Kom i gang» chooses the template for the user, who can still change it */
  preselectTemplate?: boolean;
};

type TemplateField =
  | 'psychologicalSafetyTemplateId'
  | 'workEnvironmentTemplateId'
  | 'customTemplateId';

const TEMPLATE_FIELD: Record<SurveyFormData['surveyType'], TemplateField> = {
  'psychological-safety': 'psychologicalSafetyTemplateId',
  'work-environment': 'workEnvironmentTemplateId',
  custom: 'customTemplateId',
};

/**
 * Helper function to extract question text from different template types.
 * Handles inconsistent property names across SurveyTemplate and HMS questions (text)
 * and CustomTemplate (questionText).
 */
function getQuestionText(question: any, t: (key: string) => string): string {
  if ('text' in question && question.text) {
    return question.text;
  }
  if ('questionText' in question && question.questionText) {
    return question.questionText;
  }
  return t('surveyCreation.setup.unknownQuestion');
}

export const SurveySetup = ({
  handleChange,
  formData,
  teams,
  errors,
  preselectTemplate = false,
}: SurveySetupProps) => {
  const { t } = useTranslation();
  const { hasFeature } = useFeatureFlag();
  const {
    data: psychologicalSafetyTemplates,
    isLoading: isLoadingPsychTemplates,
    refetch: refetchPsychTemplates,
  } = useGetSurveyTemplatesQuery();
  const {
    data: workEnvironmentTemplates,
    isLoading: isLoadingTemplates,
    refetch: refetchWorkEnvTemplates,
  } = useGetWorkEnvironmentTemplatesQuery(
    formData.surveyType === 'work-environment',
  );
  const {
    data: customTemplates,
    isLoading: isLoadingCustomTemplates,
    refetch: refetchCustomTemplates,
  } = useGetCustomSurveyTemplatesQuery({
    teamId: formData.team,
  });

  const surveyType = formData.surveyType || 'psychological-safety';
  const templateField = TEMPLATE_FIELD[surveyType];
  const selectedTemplateId = formData[templateField];

  const templateOptions: Array<{ label: string; value: number }> =
    surveyType === 'psychological-safety'
      ? (psychologicalSafetyTemplates ?? []).map((template) => ({
          label: `${template.name} (${t('surveyCreation.setup.questionsCount', { count: template.questions?.length || 0 })})`,
          value: template.id,
        }))
      : surveyType === 'work-environment'
        ? (workEnvironmentTemplates ?? []).map((version) => ({
            label: templateVersionLabel(version, t),
            value: version.id,
          }))
        : (customTemplates ?? [])
            .filter((template) => !template.isArchived)
            .map((template) => ({
              label: `${template.name}${template.category ? ` - ${template.category}` : ''} (${t('surveyCreation.setup.questionsCount', { count: template.questions?.length || 0 })})`,
              value: template.id,
            }));

  const isLoadingTemplateOptions =
    surveyType === 'psychological-safety'
      ? isLoadingPsychTemplates
      : surveyType === 'work-environment'
        ? isLoadingTemplates
        : isLoadingCustomTemplates;

  const selectedWorkEnvironmentTemplate = workEnvironmentTemplates?.find((t) => t.id === selectedTemplateId);
  const selectedTemplate =
    surveyType === 'psychological-safety'
      ? psychologicalSafetyTemplates?.find((t) => t.id === selectedTemplateId)
      : customTemplates?.find((t) => t.id === selectedTemplateId);

  const questions: any[] =
    surveyType === 'work-environment'
      ? selectedWorkEnvironmentTemplate
        ? templateVersionQuestions(selectedWorkEnvironmentTemplate)
        : []
      : (selectedTemplate?.questions ?? []);

  const recommendedWorkEnvironmentTemplate = workEnvironmentTemplates && defaultTemplateVersion(workEnvironmentTemplates);
  useEffect(() => {
    const first = psychologicalSafetyTemplates?.[0]?.id;
    if (preselectTemplate && surveyType === 'psychological-safety' && !formData.psychologicalSafetyTemplateId && first) {
      handleChange('psychologicalSafetyTemplateId', first);
    }
  }, [preselectTemplate, surveyType, formData.psychologicalSafetyTemplateId, psychologicalSafetyTemplates, handleChange]);

  useEffect(() => {
    if (surveyType === 'work-environment' && !formData.workEnvironmentTemplateId && recommendedWorkEnvironmentTemplate) {
      handleChange('workEnvironmentTemplateId', recommendedWorkEnvironmentTemplate);
    }
  }, [surveyType, formData.workEnvironmentTemplateId, recommendedWorkEnvironmentTemplate, handleChange]);

  const surveyTypeOptions = [
    ...(hasFeature('PSYCHOLOGICAL_SAFETY')
      ? [{ label: t('surveyCreation.setup.surveyTypes.psychologicalSafety'), value: 'psychological-safety' }]
      : []),
    ...(hasFeature('WORK_ENVIRONMENT')
      ? [{ label: t('surveyCreation.setup.surveyTypes.workEnvironment'), value: 'work-environment' }]
      : []),
    ...(hasFeature('CUSTOM_SURVEYS')
      ? [{ label: t('surveyCreation.setup.surveyTypes.custom'), value: 'custom' }]
      : []),
  ];

  const onSurveyTypeChange = (value: string) => {
    handleChange('surveyType', value);
    // Clear the template of the other survey types
    Object.values(TEMPLATE_FIELD)
      .filter((field) => field !== TEMPLATE_FIELD[value as SurveyFormData['surveyType']])
      .forEach((field) => handleChange(field, undefined));
    refetchPsychTemplates();
    refetchWorkEnvTemplates();
    refetchCustomTemplates();
  };

  return (
    <div className={classes.container}>
      <div className={classes.grid}>
        <WizardField id="choose-team" label={t('surveyCreation.setup.team')} error={errors?.team}>
          {(control) => (
            <select
              {...control}
              className="ds-input"
              name="team"
              value={String(formData.team)}
              onChange={(e) => handleChange('team', parseInt(e.target.value))}
            >
              {!teams.some((team) => team.id === formData.team) && (
                <option value="0" disabled>
                  {t('surveyCreation.setup.team')}
                </option>
              )}
              {teams.map((team) => (
                <option key={team.id} value={team.id}>
                  {team.name}
                </option>
              ))}
            </select>
          )}
        </WizardField>

        <WizardField id="survey-type" label={t('surveyCreation.setup.type')} error={errors?.surveyType}>
          {(control) => (
            <select
              {...control}
              className="ds-input"
              name="surveyType"
              value={surveyType}
              onChange={(e) => onSurveyTypeChange(e.target.value)}
            >
              {surveyTypeOptions.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          )}
        </WizardField>

        <WizardField
          id="survey-template"
          label={t('surveyCreation.setup.template')}
          description={t('surveyCreation.setup.templateDescription')}
          error={errors?.[templateField]}
        >
          {(control) =>
            isLoadingTemplateOptions ? (
              <GhostLoader style={{ width: '100%', height: 40 }} />
            ) : templateOptions.length > 0 ? (
              <select
                {...control}
                className="ds-input"
                name={templateField}
                value={selectedTemplateId ? String(selectedTemplateId) : ''}
                onChange={(e) =>
                  handleChange(
                    templateField,
                    e.target.value ? parseInt(e.target.value) : undefined,
                  )
                }
              >
                <option value="">{t('surveyCreation.setup.templatePlaceholder')}</option>
                {templateOptions.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            ) : (
              <div className="ds-alert" data-color="warning" data-size="sm">
                <h3 className={`ds-heading ${classes.alertHeading}`} data-size="2xs">
                  {t('surveyCreation.setup.noTemplates.title')}
                </h3>
                <p className="ds-paragraph" data-size="sm">
                  {surveyType === 'custom'
                    ? t('surveyCreation.setup.noTemplates.customDescription')
                    : t('surveyCreation.setup.noTemplates.description')}
                </p>
              </div>
            )
          }
        </WizardField>

        <WizardField
          id="survey-name"
          label={t('surveyCreation.setup.name')}
          description={t('surveyCreation.setup.nameDescription')}
          error={errors?.name}
        >
          {(control) => (
            <input
              {...control}
              className="ds-input"
              type="text"
              name="name"
              autoComplete="off"
              placeholder={t('surveyCreation.setup.namePlaceholder')}
              value={formData.name}
              onChange={(e) => handleChange('name', e.target.value)}
            />
          )}
        </WizardField>
      </div>

      {questions.length > 0 && (
        <details className="ds-details" data-variant="tinted">
          <summary>{t('surveyCreation.setup.viewQuestions')}</summary>
          <div>
            <ol className={classes.questionsList}>
              {questions.map((question, index) => (
                <li key={index} className="ds-paragraph" data-size="sm">
                  {getQuestionText(question, t)}
                </li>
              ))}
            </ol>
          </div>
        </details>
      )}
    </div>
  );
};
